import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiTrash2, FiEdit2, FiCheck, FiSquare, FiCheckSquare, FiAlertCircle } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../utils/api';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import DeleteTodoModal from '../modals/DeleteTodoModal';
import RichTextEditor from '../messages/RichTextEditor';
import SuggestionDropdown from '../shared/SuggestionDropdown';
import DateDropdown from '../shared/DateDropdown';
import { filterDateCommands } from '../../utils/dateCommandUtils';
import { useMessages } from '../../context/MessagesContext';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { usePlanPermissions, getCurrentPlan, UpgradeMessage } from '../../utils/planPermissions';
import { resolveDateTags } from '../../utils/dateCommandUtils';

const apiBase = process.env.REACT_APP_API_URL || '/api';

const TodoPanel = ({ open, onClose, highlightId }) => {
    const [items, setItems] = useState([]);
    const [text, setText] = useState('');
    const [author, setAuthor] = useState('');
    const itemRefs = useRef({});
    const { getAuthHeaders, user, activeTenantId } = useAuth();
    const currentUserId = user?._id || user?.id || null;

    // Permissions basées sur le plan
    const currentPlan = getCurrentPlan(user, activeTenantId);
    const { canMentionUsers, canMentionTodos, canMentionMessages, canUseDateCommands } = usePlanPermissions(currentPlan);

    const [allMessages, setAllMessages] = useState([]);
    const [allTodos, setAllTodos] = useState([]);
    const [allUsers, setAllUsers] = useState([]);

    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');
    const { handleApiError } = useApiErrorHandler();
    const { getMessageById } = useMessages();
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedTodos, setSelectedTodos] = useState(new Set());
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [todosToDelete, setTodosToDelete] = useState([]);

    // États pour les suggestions mentions
    const [suggestOpen, setSuggestOpen] = useState(false);
    const [suggestItems, setSuggestItems] = useState([]);
    const [suggestAnchor, setSuggestAnchor] = useState(null);
    const [suggestActive, setSuggestActive] = useState(0);
    const editorRef = useRef(null);
    const [limitError, setLimitError] = useState(null);

    // États pour les dates dynamiques
    const [dateSuggestOpen, setDateSuggestOpen] = useState(false);
    const [dateSuggestItems, setDateSuggestItems] = useState([]);
    const [dateSuggestAnchor, setDateSuggestAnchor] = useState(null);
    const [dateSuggestActive, setDateSuggestActive] = useState(0);

    // Quota enforcement: check if tenant is over free plan limits
    const { tenant } = useAuth();
    const isFreePlan = tenant?.plan === 'free';
    const planLimits = tenant?.planLimits || {};
    const resourceCounts = tenant?.resourceCounts || {};
    const overQuota = isFreePlan && (
        (planLimits.maxTodos && resourceCounts.todos >= planLimits.maxTodos) ||
        (planLimits.maxMessages && resourceCounts.messages > planLimits.maxMessages) ||
        (planLimits.maxMembers && resourceCounts.members > planLimits.maxMembers)
    );

    useEffect(() => {
        if (user?.name) {
            setAuthor(user.name);
        }
    }, [user]);

    useEffect(() => {
        if (!open) return;

        const fetchData = async () => {
            try {
                const data = await apiFetch(
                    `${apiBase}/todos`,
                    { headers: getAuthHeaders() },
                    handleApiError
                );
                setItems(data || []);
            } catch (err) {
                console.error('Error fetching todos:', err);
            }
        };

        fetchData();
    }, [open, getAuthHeaders, handleApiError]);

    useEffect(() => {
        if (open && highlightId && items.length > 0) {
            setTimeout(() => {
                const el = itemRefs.current[highlightId];
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
        }
    }, [open, highlightId, items]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                if (selectionMode) {
                    setSelectionMode(false);
                    setSelectedTodos(new Set());
                } else if (editingId) {
                    cancelEdit();
                } else {
                    onClose();
                }
            }
        };

        if (open) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => document.removeEventListener('keydown', handleEscape);
    }, [open, onClose, selectionMode, editingId]);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const [mRes, tRes, uRes] = await Promise.all([
                    fetch(`${apiBase}/messages`, { headers: getAuthHeaders() }),
                    fetch(`${apiBase}/todos`, { headers: getAuthHeaders() }),
                    fetch(`${apiBase}/tenants/${activeTenantId}/users`, { headers: getAuthHeaders() })
                ]);

                if (mRes.ok) setAllMessages(await mRes.json());
                if (tRes.ok) setAllTodos(await tRes.json());
                if (uRes.ok) setAllUsers(await uRes.json());
            } catch (e) {
                console.error('Error fetching data:', e);
            }
        })();
    }, [open, getAuthHeaders, activeTenantId]);

    const handleMentionTrigger = (data) => {
        // Vérifier que le système de suggestions est actif
        if (!data.isActive) {
            setSuggestOpen(false);
            return;
        }

        const query = (data.query || '').toLowerCase();

        const msgs = canMentionMessages ? (allMessages || [])
            .filter(m => m.title?.toLowerCase().includes(query) || m.content?.toLowerCase().includes(query))
            .slice(0, 20)
            .map(m => ({ ...m, kind: 'msg' })) : [];

        const todos = canMentionTodos ? (allTodos || [])
            .filter(t => t.text?.toLowerCase().includes(query))
            .slice(0, 20)
            .map(t => ({ ...t, kind: 'todo' })) : [];

        // Filtrer les utilisateurs selon le plan
        const users = canMentionUsers ? (allUsers || [])
            .filter(u => u.name?.toLowerCase().includes(query) || u.email?.toLowerCase().includes(query))
            .slice(0, 20)
            .map(u => ({ ...u, kind: 'user' })) : [];

        const items = [...msgs, ...todos, ...users];
        setSuggestItems(items);
        setSuggestActive(0);
        setSuggestOpen(items.length > 0);
        setSuggestAnchor(data.rect);
    };

    const handleMentionClick = () => {
        if (editorRef.current) {
            const editor = editorRef.current;
            
            // Insert @ character
            if (editor.insertContent) {
                editor.insertContent('@');
            }
            
            // Trigger mention dropdown manually
            handleMentionTrigger({ isActive: true, query: '', rect: null });
        }
    };

    const applySuggestion = (item) => {
        let mention = '';
        if (item.kind === 'msg') {
            mention = `@msg:${item._id}`;
        } else if (item.kind === 'todo') {
            mention = `@todo:${item._id}`;
        } else if (item.kind === 'user') {
            mention = `@user:${item._id}[${item.name}]`;
        }

        if (editorRef.current?.insertMention) {
            editorRef.current.insertMention(mention);
        }

        setSuggestOpen(false);

        setTimeout(() => {
            editorRef.current?.focus();
        }, 0);
    };

    const handleDateTrigger = (data) => {
        if (!data.isActive) {
            setDateSuggestOpen(false);
            return;
        }
        const items = filterDateCommands(data.query || '');
        setDateSuggestItems(items);
        setDateSuggestActive(0);
        setDateSuggestOpen(items.length > 0);
        setDateSuggestAnchor(data.rect);
    };

    const applyDateSuggestion = (item) => {
        if (editorRef.current?.insertDateCommand) {
            // Insère une balise persistante {{date:Commande}} résolue dynamiquement à l'affichage
            editorRef.current.insertDateCommand(`{{date:${item.command}}}`);
        }
        setDateSuggestOpen(false);
        setTimeout(() => editorRef.current?.focus(), 0);
    };

    // Navigation clavier avec preventDefault sur Enter
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!suggestOpen) return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    e.stopPropagation();
                    setSuggestActive(prev => Math.min(prev + 1, suggestItems.length - 1));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    e.stopPropagation();
                    setSuggestActive(prev => Math.max(prev - 1, 0));
                    break;
                case 'Enter':
                    if (suggestItems[suggestActive]) {
                        e.preventDefault();
                        e.stopPropagation();
                        applySuggestion(suggestItems[suggestActive]);
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    e.stopPropagation();
                    setSuggestOpen(false);
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [suggestOpen, suggestItems, suggestActive]);

    const startEdit = (item) => {
        setEditingId(item._id);
        setEditText(item.text);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditText('');
    };

    const saveEdit = async (id) => {
        if (!editText.trim()) return;

        try {
            const updated = await apiFetch(
                `${apiBase}/todos/${id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...getAuthHeaders()
                    },
                    body: JSON.stringify({ text: editText })
                },
                handleApiError
            );

            setItems(prev => prev.map(item => item._id === id ? updated : item));
            cancelEdit();
        } catch (err) {
            console.error('Error updating todo:', err);
        }
    };

    const [loading, setLoading] = useState(false);
    const createLock = useRef(false);
    const addItem = async (e) => {
        e.preventDefault();
        if (loading || createLock.current) return;
        if (!text || !text.trim() || text.replace(/<[^>]*>/g, '').trim().length === 0) return;

        setLimitError(null);
        setLoading(true);
        createLock.current = true;
        try {
            const newItem = await apiFetch(
                `${apiBase}/todos`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...getAuthHeaders()
                    },
                    body: JSON.stringify({ text, author })
                },
                handleApiError
            );

            setItems(prev => [newItem, ...prev]);
            setText('');
        } catch (err) {
            console.error('Error creating todo:', err);
            if (err.code === 'LIMIT_REACHED') {
                setLimitError(err.message);
            }
        } finally {
            setLoading(false);
            createLock.current = false;
        }
    };

    const toggleDone = async (id, done) => {
        try {
            const updated = await apiFetch(
                `${apiBase}/todos/${id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...getAuthHeaders()
                    },
                    body: JSON.stringify({ done })
                },
                handleApiError
            );

            setItems(prev => prev.map(item => item._id === id ? updated : item));
        } catch (err) {
            console.error('Error toggling todo:', err);
        }
    };

    const handleDeleteClick = (todo) => {
        setTodosToDelete([todo]);
        setShowDeleteModal(true);
    };

    const handleDeleteSelected = () => {
        const todosArray = items.filter(item => selectedTodos.has(item._id));
        setTodosToDelete(todosArray);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        try {
            await Promise.all(
                todosToDelete.map(todo =>
                    fetch(`${apiBase}/todos/${todo._id}`, {
                        method: 'DELETE',
                        headers: getAuthHeaders()
                    })
                )
            );

            const deletedIds = new Set(todosToDelete.map(t => t._id));
            setItems(prev => prev.filter(item => !deletedIds.has(item._id)));
            setSelectedTodos(new Set());
            setSelectionMode(false);
        } catch (err) {
            console.error('Error deleting todos:', err);
        } finally {
            setShowDeleteModal(false);
            setTodosToDelete([]);
        }
    };

    const handleCheckboxClick = (item) => {
        if (selectionMode) {
            // En mode sélection : toggle la sélection
            toggleSelection(item._id);
        } else {
            // Hors mode sélection : toggle done
            toggleDone(item._id, !item.done);
        }
    };

    const toggleSelection = (todoId) => {
        setSelectedTodos(prev => {
            const newSet = new Set(prev);
            if (newSet.has(todoId)) {
                newSet.delete(todoId);
            } else {
                newSet.add(todoId);
            }
            return newSet;
        });
    };

    const selectAll = () => {
        setSelectedTodos(new Set(items.map(item => item._id)));
    };

    const deselectAll = () => {
        setSelectedTodos(new Set());
    };

    const renderContentWithMentions = (html) => {
        let content = html || '';

        // Résolution dynamique des balises de dates {{date:Commande}}
        content = resolveDateTags(content);

        // Remplacer les mentions @user:id[Name]
        content = content.replace(/@user:([A-Za-z0-9_-]+)\[([^\]]+)\]/g, (m, id, name) => {
            const isMentioned = id === currentUserId;
            const mentionClass = isMentioned ? 'user-mention mentioned-user' : 'user-mention';
            return `<span class="${mentionClass}" data-user-id="${id}">${name}</span>`;
        });

        // Remplacer les mentions @todo:
        content = content.replace(/@todo:([A-Za-z0-9_-]+)/g, (m, id) => {
            const todo = allTodos?.find(t => t._id === id);
            const stripHtmlSimple = (html) => {
                const div = document.createElement('div');
                div.innerHTML = sanitizeHtml(html || '');
                return div.textContent || div.innerText || '';
            };
            const label = stripHtmlSimple(todo?.text) || `todo:${id}`;
            const truncated = label.length > 40 ? label.slice(0, 40) + '...' : label;
            return `<a href="app://todo/${id}" class="mention-link text-accent underline hover:opacity-80 font-semibold" data-mention-type="todo" data-mention-id="${id}">${truncated}</a>`;
        });

        // Remplacer les mentions @msg:
        content = content.replace(/@msg:([A-Za-z0-9_-]+)/g, (m, id) => {
            const msg = allMessages?.find(m => m._id === id);
            const label = msg?.title || `msg:${id}`;
            const truncated = label.length > 40 ? label.slice(0, 40) + '...' : label;
            return `<a href="app://msg/${id}" class="mention-link text-accent underline hover:opacity-80 font-semibold" data-mention-type="msg" data-mention-id="${id}">${truncated}</a>`;
        });

        return content;
    };

    const handleContentClick = (e) => {
        const target = e.target;
        if (target.classList.contains('mention-link')) {
            e.preventDefault();
            e.stopPropagation();

            const mentionType = target.getAttribute('data-mention-type');
            const mentionId = target.getAttribute('data-mention-id');

            if (mentionType === 'todo') {
                // Trouver et scroller vers la todo dans la liste actuelle
                const todoItem = itemRefs.current[mentionId];
                if (todoItem) {
                    todoItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    todoItem.classList.add('ring-2', 'ring-accent', 'bg-yellow-50', 'dark:bg-yellow-900/20');
                    setTimeout(() => {
                        todoItem.classList.remove('ring-2', 'ring-accent', 'bg-yellow-50', 'dark:bg-yellow-900/20');
                    }, 2000);
                } else {
                    console.log('Todo not found in current list');
                }
            } else if (mentionType === 'msg') {
                const targetMessage = getMessageById(mentionId);
                if (targetMessage) {
                    onClose();
                    setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('openMsg', { detail: { id: mentionId } }));
                    }, 100);
                }
            }
        }
    };

    if (!open) return null;

    return (
        <>
            {/* Overlay */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 z-[999]"
                onClick={onClose}
            />

            {/* Panel latéral */}
            <div className="fixed right-0 top-0 h-full w-full sm:w-[600px] bg-white dark:bg-gray-800 shadow-2xl z-[9999] flex flex-col animate-slideIn">
                {/* Header */}
                <div className="p-4 sm:p-6 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                    {/* Première ligne : Titre + bouton fermer */}
                    <div className="flex items-center justify-between mb-3 sm:mb-0">
                        <div>
                            <h2 className="text-xl sm:text-2xl font-bold text-black dark:text-white">Todos</h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{items.length} tâche(s)</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 sm:hidden"
                        >
                            <FiX className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Deuxième ligne : Boutons d'action */}
                    <div className="flex items-center gap-2 flex-wrap sm:absolute sm:top-6 sm:right-6">
                        {selectionMode ? (
                            <>
                                <button
                                    onClick={selectAll}
                                    className="text-sm px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
                                >
                                    Tout
                                </button>
                                <button
                                    onClick={deselectAll}
                                    className="text-sm px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
                                >
                                    Aucun
                                </button>
                                <button
                                    onClick={handleDeleteSelected}
                                    disabled={selectedTodos.size === 0}
                                    className="text-sm px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Supprimer ({selectedTodos.size})
                                </button>
                                <button
                                    onClick={() => {
                                        setSelectionMode(false);
                                        setSelectedTodos(new Set());
                                    }}
                                    className="text-sm px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
                                >
                                    Annuler
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={() => setSelectionMode(true)}
                                className="text-sm px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
                            >
                                Sélection
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="hidden sm:block text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2"
                        >
                            <FiX className="h-6 w-6" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Add form */}
                    <form onSubmit={addItem} className="mb-6">
                        <div className="mb-3">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Nouvelle tâche
                            </label>
                            {overQuota && (
                                <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded flex items-center gap-2">
                                    <FiAlertCircle className="h-5 w-5 text-red-600 dark:text-red-500 flex-shrink-0" />
                                    <div className="text-sm text-red-800 dark:text-red-200">
                                        <strong>Limite du plan gratuit dépassée.</strong> Supprimez des tâches, messages ou membres pour pouvoir ajouter de nouvelles tâches.
                                    </div>
                                </div>
                            )}
                            <RichTextEditor
                                ref={editorRef}
                                content={text}
                                onChange={setText}
                                placeholder={canMentionUsers || canMentionTodos || canMentionMessages
                                    ? (canUseDateCommands ? "Décrivez votre tâche... @ pour mentionner, // pour une date" : "Décrivez votre tâche... Utilisez @ pour mentionner")
                                    : (canUseDateCommands ? "Décrivez votre tâche... // pour insérer une date" : "Décrivez votre tâche...")}
                                minHeight="100px"
                                onMentionTrigger={handleMentionTrigger}
                                onMentionClick={handleMentionClick}
                                hasMentionSupport={canMentionUsers || canMentionTodos || canMentionMessages}
                                onDateTrigger={canUseDateCommands ? handleDateTrigger : undefined}
                                disabled={overQuota || loading}
                            />
                            {!canMentionMessages && !canMentionTodos && !canMentionUsers ? (
                                <UpgradeMessage
                                    feature="todoToMessage"
                                    currentPlan={currentPlan}
                                    className="mt-2"
                                />
                            ) : (
                                <>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        💡 @ pour mentionner {[
                                            canMentionMessages && 'des messages',
                                            canMentionTodos && 'des todos',
                                            canMentionUsers && 'des utilisateurs'
                                        ].filter(Boolean).join(' et ')}{canUseDateCommands ? ' · // pour une date dynamique (ex : //Mois-1)' : ''}
                                    </p>
                                    {canMentionMessages && canMentionTodos && !canMentionUsers && (
                                        <UpgradeMessage
                                            feature="userMentions"
                                            currentPlan={currentPlan}
                                            className="mt-2"
                                        />
                                    )}
                                </>
                            )}
                            {limitError && (
                                <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                                    <FiAlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                        <p className="text-sm text-red-800 dark:text-red-300 font-medium">
                                            {limitError}
                                        </p>
                                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                                            Supprimez des todos existantes ou passez à un plan supérieur
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                        <button
                            type="submit"
                            disabled={!text || !text.trim() || text.replace(/<[^>]*>/g, '').trim().length === 0 || overQuota}
                            className="w-full bg-primary text-white dark:text-black font-bold py-2 px-4 rounded hover:bg-primary-hover transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Ajouter
                        </button>
                    </form>
                    {/* Liste des todos */}
                    {items.map(item => (
                        <div className="space-y-3 mb-2" key={item._id}>
                            <div
                                key={item._id}
                                ref={el => { if (el) itemRefs.current[item._id] = el; }}
                                className={`p-4 border dark:border-gray-700 rounded-lg transition ${highlightId === item._id ? 'ring-2 ring-accent bg-yellow-50 dark:bg-yellow-900/20' : ''
                                    } ${item.done && !selectionMode ? 'bg-gray-50 dark:bg-gray-700/50' : 'bg-white dark:bg-gray-800'
                                    } ${selectionMode && selectedTodos.has(item._id) ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''
                                    }`}
                            >
                                <div className="flex items-start gap-3">
                                    {/* Tous les membres peuvent toggle done/undone */}
                                    <button
                                        onClick={() => handleCheckboxClick(item)}
                                        className="flex-shrink-0 mt-1"
                                        title={selectionMode ? (selectedTodos.has(item._id) ? "Désélectionner" : "Sélectionner") : (item.done ? "Marquer comme non fait" : "Marquer comme fait")}
                                        disabled={selectionMode ? false : false}
                                    >
                                        {selectionMode ? (
                                            selectedTodos.has(item._id) ? (
                                                <FiCheckSquare className="h-5 w-5 text-blue-500" />
                                            ) : (
                                                <FiSquare className="h-5 w-5 text-gray-400" />
                                            )
                                        ) : (
                                            item.done ? (
                                                <FiCheckSquare className="h-5 w-5 text-green-500" />
                                            ) : (
                                                <FiSquare className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                                            )
                                        )}
                                    </button>

                                    <div className="flex-1 min-w-0">
                                        {editingId === item._id ? (
                                            <div className="space-y-2">
                                                <RichTextEditor
                                                    ref={editorRef}
                                                    content={editText}
                                                    onChange={setEditText}
                                                    placeholder="Modifier la tâche..."
                                                    minHeight="100px"
                                                    onMentionTrigger={handleMentionTrigger}
                                                    onMentionClick={handleMentionClick}
                                                    hasMentionSupport={canMentionUsers || canMentionTodos || canMentionMessages}
                                                    onDateTrigger={handleDateTrigger}
                                                />
                                                <div className="flex gap-2 justify-end">
                                                    <button
                                                        onClick={() => saveEdit(item._id)}
                                                        className="text-xs bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                                                    >
                                                        <FiCheck className="inline h-3 w-3" /> Enregistrer
                                                    </button>
                                                    <button
                                                        onClick={cancelEdit}
                                                        className="text-xs bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 px-3 py-1 rounded"
                                                    >
                                                        Annuler
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div
                                                    className={`prose prose-sm dark:prose-invert max-w-none text-gray-900 dark:text-gray-100 ${item.done && !selectionMode ? 'line-through opacity-60' : ''
                                                        }`}
                                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderContentWithMentions(item.text)) }}
                                                    onClick={handleContentClick}
                                                />
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                                    {item.author} • {new Date(item.createdAt).toLocaleDateString('fr-FR')}
                                                </p>
                                            </>
                                        )}
                                    </div>

                                    {/* Seul le créateur peut éditer et supprimer */}
                                    {!selectionMode && editingId !== item._id && String(item.createdBy) === String(currentUserId) && (
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => startEdit(item)}
                                                className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                                                title="Modifier"
                                            >
                                                <FiEdit2 className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteClick(item)}
                                                className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                                                title="Supprimer"
                                            >
                                                <FiTrash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div >

            <><SuggestionDropdown
                open={suggestOpen}
                items={suggestItems}
                onSelect={applySuggestion}
                anchorRect={suggestAnchor}
                activeIndex={suggestActive}
                onActiveIndexChange={setSuggestActive} />
            {canUseDateCommands && (
                <DateDropdown
                    open={dateSuggestOpen}
                    items={dateSuggestItems}
                    onSelect={applyDateSuggestion}
                    anchorRect={dateSuggestAnchor}
                    activeIndex={dateSuggestActive}
                    onActiveIndexChange={setDateSuggestActive}
                />
            )}<DeleteTodoModal
                    open={showDeleteModal}
                    onClose={() => {
                        setShowDeleteModal(false);
                        setTodosToDelete([]);
                    }}
                    todos={todosToDelete}
                    onConfirm={confirmDelete}
                    isMultiple={todosToDelete.length > 1} /></>
        </>
    );
};

export default TodoPanel;